// Hook that hydrates redux from any in-flight Notabene transfer the user has
// on the server. Runs once on mount when the user is authenticated; covers
// the cases where localStorage was cleared (different device, private mode,
// manual cache clear) so the user can still resume an authorized verification
// without re-doing the SDK flow.

import { useEffect, useRef } from "react";
import { useGetNotabeneTransfersQuery } from "src/store/features/api/notabene";
import {
  setNotabeneTxId,
  setNotabeneStatus,
  setNotabenePolicies,
  setNotabenePiiSubmitted,
  resetNotabeneState,
  resetWithdrawalState,
  setToken,
  setWalletAddress,
  setDisplayWalletAddress,
  setWithdrawalAmount,
  setCryptoWithdrawalStage,
  useWithdrawalSlice,
  NotabeneStatus,
  NotabenePolicy,
} from "src/store/features/withdrawal.slice";
import { useAppDispatch } from "src/store/store";
import { useAuth } from "src/providers/AuthProvider";
import { useUserSlice } from "src/store/features/user.slice";
import { rejectionDismissedKey, isRejectedStatus } from "./TravelRuleStatusBanner";

const ACTIVE_STATUSES = new Set(["pending", "authorized", "ack"]);

// Compliance-decline detection lives in `isRejectedStatus` (single source of
// truth, shared with the banner). It matches `rejected`/`tr_declined`/`denied`
// and deliberately EXCLUDES `canceled`/`cancelled` — a user-initiated Cancel
// sets the server transfer to CANCELED, and re-surfacing that as a "regulatory
// rejection" banner on a later load would be wrong.

const piiSatisfied = (t: { policies?: NotabenePolicy[] }): boolean =>
  !!t.policies?.some((p) => p.type === "RequirePresentation" && p.status === "satisfied");

interface NotabeneTransfer {
  txId: string;
  status: string;
  /** OUTGOING (withdrawal) vs INCOMING (deposit) — the outbound banners must
   * only ever surface OUTGOING transfers. */
  direction?: string;
  policies?: NotabenePolicy[];
  errorMessage?: string;
  asset?: string;
  amount?: string;
  destinationAddress?: string;
  // Set once the transfer's withdrawal has been submitted (linked server-side).
  // When present, the withdrawal is already done — never resume it.
  withdrawalId?: string;
}

/**
 * Returns the most recent transfer that's awaiting user action — the one we
 * should resume if the user reopens the modal. We treat a transfer as
 * resumable when its status is non-terminal AND its `RequirePresentation`
 * policy is satisfied (meaning the SDK has already collected PII and we're
 * just waiting on counterparty/compliance).
 */
export const pickResumableTransfer = (transfers: NotabeneTransfer[]): NotabeneTransfer | undefined => {
  return transfers.find((t) => {
    // OUTBOUND only. The "continue your withdrawal" banner resumes a
    // withdrawal — an INCOMING Deposit Assist transfer that reaches
    // AUTHORIZED (self-declaration released the hold) would otherwise match
    // the authorized branch below and surface the wrong banner, whose Cancel
    // fires cancelActiveNotabeneTransfers and DECLINES the just-completed
    // deposit (prod, 2026-08-08 15:45Z, txId a3082eac). Deposits have their
    // own DepositActionRequiredBanner; this one is withdrawals only.
    if (t.direction === "INCOMING") return false;
    // Already submitted: the transfer was linked to a processed withdrawal, so
    // there's nothing for the user to resume — skip it regardless of the
    // Notabene status (which stays AUTHORIZED in our off-platform model, since
    // we never call Notabene's Settle). This is what prevents the stale
    // "continue your withdrawal" banner for a completed withdrawal, and
    // mirrors the backend's linked-withdrawalId short-circuit in the
    // WithdrawalOrchestrator / Notabene dispatch listener.
    if (t.withdrawalId) return false;
    const status = t.status?.toLowerCase();
    if (!status || !ACTIVE_STATUSES.has(status)) return false;
    // Authorized status implies PII was satisfied earlier; otherwise require
    // the explicit policy signal so we don't surface pre-PII transfers (the
    // user would still need to redo the SDK flow for those, so resuming is
    // pointless).
    return status === "authorized" || piiSatisfied(t);
  });
};

/**
 * Returns a terminally-rejected transfer the user still needs to be told about
 * — declined after PII was submitted (i.e. it reached compliance review) and
 * not already dismissed. We require `piiSatisfied` so a pre-PII abandon/cancel
 * never surfaces as a "regulatory rejection" banner, and we skip dismissed
 * ones so the terminal banner doesn't re-appear on every load/refresh.
 */
export const pickRejectedTransfer = (transfers: NotabeneTransfer[]): NotabeneTransfer | undefined => {
  return transfers.find((t) => {
    // Withdrawals only — same reasoning as pickResumableTransfer. A declined
    // inbound deposit must not raise the outbound "regulatory rejection"
    // banner (the deposit path owns its own messaging).
    if (t.direction === "INCOMING") return false;
    if (!isRejectedStatus(t.status)) return false;
    if (!piiSatisfied(t)) return false;
    const dismissed =
      typeof window !== "undefined" &&
      window.localStorage.getItem(rejectionDismissedKey(t.txId)) === "1";
    return !dismissed;
  });
};

export const useTravelRuleHydrate = (enabled = true): void => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();
  const { notabeneTxId, selectedToken, withdrawalPreparedToken } = useWithdrawalSlice();
  const {
    details: { customerId },
  } = useUserSlice();
  // Which profile the last reconcile ran for. Keyed by customerId (not a
  // boolean) because one login can hold several customer profiles
  // (`account.selected`/`default` server-side) and both the persisted TR
  // slice and `/notabene/transfers` are PROFILE-scoped: after a profile
  // switch the previous reconcile is void and must run again, otherwise the
  // banner keeps rendering the OTHER profile's in-flight transfer — which
  // this profile can't poll, resume, or cancel (every call 404s on the
  // ownership guard; prod incident 2026-07-13, the /notabene/transfer 4xx
  // alert).
  const hydratedForRef = useRef<string | null>(null);

  // Always reconcile against the server once per mount per profile (guarded
  // by `hydratedForRef`, so it's a single call, not per page-load). We
  // deliberately DON'T skip merely because redux already holds a persisted
  // transfer: that "save a network call" optimization is exactly what
  // stranded the "continue your withdrawal" banner on a completed withdrawal
  // — the server knows the transfer was submitted (via its `withdrawalId`),
  // but the skip meant `pickResumableTransfer`'s withdrawalId guard never
  // ran, so the banner rendered from stale localStorage. The server is the
  // source of truth.
  // Still skip while unauthenticated: this hook is mounted in the global
  // `Layout`, so a logout transient or stale-token re-focus (RTK base config
  // sets `refetchOnFocus: true`) would otherwise trip `AuthnMiddleware`'s 401
  // path on `/notabene/transfers`. Also skip until the active profile's
  // customerId is known — the reconcile result is only meaningful relative
  // to a specific profile.
  const skip =
    !enabled || !isAuthenticated || !customerId || hydratedForRef.current === customerId;
  // `customerId` keys the cache per-profile, and `currentData` (not `data`)
  // guarantees the list we reconcile against was fetched FOR that key: after
  // a profile switch changes the arg, `data` would still surface the
  // previous profile's fulfilled list while the new profile's fetch is in
  // flight — reconciling against that would mark the new profile hydrated
  // off the other profile's transfers and write the wrong transfer straight
  // back into the slice, undoing the switch-time clear.
  const { currentData: transfers } = useGetNotabeneTransfersQuery(customerId, { skip });

  useEffect(() => {
    if (skip || !transfers) return;
    const list = transfers as NotabeneTransfer[];
    const resumable = pickResumableTransfer(list);
    hydratedForRef.current = customerId;

    // A persisted transfer that is NOT in this profile's list belongs to a
    // different profile on the same login (or was purged server-side). The
    // banner must never render it here: this profile cannot poll, resume, or
    // cancel it — every request 404s on the per-profile ownership guard, so
    // the banner would just strand the user in a retry loop. Clear the
    // persisted slice; switching back to the initiating profile re-hydrates
    // it from that profile's own list (that's this hook's job). Known
    // limitation: an in-review (e.g. FLAGGED) transfer isn't restored on
    // switch-back until it reaches a resumable/rejected state, because the
    // picks below only cover those.
    // Tracks the clear below WITHIN this effect run: the reset nulls
    // `withdrawalPreparedToken` in the store, but the closed-over value this
    // run captured is the pre-clear one — a truthy foreign token would
    // otherwise skip the forced-Verification stage when a resumable transfer
    // is restored further down.
    let clearedForeignState = false;
    if (notabeneTxId && !list.some((t) => t.txId === notabeneTxId)) {
      // The FULL reset (not just the notabene fields): the foreign flow's
      // form inputs — token, destination address, amount — are just as
      // profile-scoped as the txId, and leaving them behind prefills the new
      // profile's next withdrawal with the other profile's destination. This
      // is the safety net for switches that bypass the account-list handlers
      // (another tab, restored localStorage), which dispatch the same reset.
      // It also puts the stage back to InputDetails and closes the modal.
      dispatch(resetWithdrawalState());
      clearedForeignState = true;
    }

    if (!resumable) {
      // No active transfer to resume — but a terminal rejection still needs to
      // surface (the persisted slice was cleared / this is a different device).
      // Hydrate just enough for `TravelRuleStatusBanner` to render its red
      // rejection variant (txId + rejected status + piiSubmitted gate).
      const rejected = pickRejectedTransfer(list);
      if (rejected) {
        dispatch(setNotabeneTxId(rejected.txId));
        dispatch(setNotabeneStatus("rejected"));
        dispatch(setNotabenePiiSubmitted(true));
        return;
      }
      // Nothing resumable and nothing to surface. Clear the persisted slice
      // ONLY when the persisted transfer has actually been submitted — i.e. it
      // carries a server-side `withdrawalId` (the same marker that
      // short-circuits `pickResumableTransfer`). This is the server-authoritative
      // correction for the case where the post-submit `resetNotabeneState()`
      // (CryptoWithdrawalModal) never persisted before a page refresh — without
      // it the banner strands on a completed withdrawal and lets the user
      // re-submit an already-linked txId.
      //
      // Do NOT clear merely because the transfer is non-resumable: a transfer in
      // compliance review (e.g. FLAGGED / TR_MANUAL_REVIEW) is not in
      // ACTIVE_STATUSES yet is still in-flight on the server, so wiping its
      // persisted state would hide the status banner for an ongoing withdrawal.
      if (notabeneTxId) {
        const persisted = list.find((t) => t.txId === notabeneTxId);
        if (persisted?.withdrawalId) {
          dispatch(resetNotabeneState());
        }
      }
      return;
    }

    dispatch(setNotabeneTxId(resumable.txId));
    dispatch(setNotabeneStatus(resumable.status.toLowerCase() as NotabeneStatus));
    if (resumable.policies) {
      dispatch(setNotabenePolicies(resumable.policies));
    }
    dispatch(setNotabenePiiSubmitted(true));

    // If form context is missing (different device / cleared localStorage),
    // stub it from the transfer record so reopening the modal can render
    // the Notabene + Review stages. We supply just enough for the Notabene
    // stage's create/resume effect to skip its early bailout — display name
    // / available balance fall back to the asset code. The closed-over
    // `selectedToken` is stale when this run just cleared foreign state (the
    // store's token was wiped above), so treat that case as missing too.
    if ((!selectedToken || clearedForeignState) && resumable.asset) {
      dispatch(
        setToken({
          code: resumable.asset,
          displayName: resumable.asset,
          available: 0,
        }),
      );
    }
    if (resumable.destinationAddress) {
      dispatch(setWalletAddress(resumable.destinationAddress));
      dispatch(setDisplayWalletAddress(resumable.destinationAddress));
    }
    if (resumable.amount) {
      const parsed = parseFloat(resumable.amount);
      if (!Number.isNaN(parsed)) dispatch(setWithdrawalAmount(parsed));
    }

    // Resumed transfers don't carry a withdrawal-prepared token — that
    // token is session-bound and may not exist (different device, cleared
    // localStorage, or this Notabene transfer predates the prepared-token
    // deployment). Force the user through Verification so they can re-issue
    // a fresh token before the final submit. If they already have a token
    // in redux (same session) we leave whatever stage was persisted intact —
    // unless this run just cleared foreign state, in which case the
    // closed-over token belongs to the OTHER profile's flow and was nulled
    // in the store above.
    if (!withdrawalPreparedToken || clearedForeignState) {
      dispatch(setCryptoWithdrawalStage("Verification"));
    }
  }, [skip, transfers, dispatch, selectedToken, withdrawalPreparedToken, notabeneTxId, customerId]);
};
